import { cachedCall } from '@/services/screenCache'
import { api } from '@/services/api'
import { ATYRAU_DISTRICTS } from '@/constants/atyrauDistricts'

/*
 * «Труд и занятость» — ВЛИ (перечни ГИТ), витрина `app.vli_statistic`.
 *
 * ТЕ ЖЕ РУЧКИ, ЧТО У САЙТА (`GroupLabor.vue`): `/vli/syncs` и
 * `/vli/statistics?subject_kind=ul|ip&extract_date=`. Раньше планшет ходил в
 * `/akim/labor/dashboard` — ту же таблицу, собранную бэкендом под планшет.
 * Цифры у двух путей совпадали до единицы (прод, 29.09.2026: ЮЛ 4 806, без ТД
 * 609, риск ЗП 2 530), поэтому сборка ниже повторяет правила
 * `labor-dashboard.service.ts` на бэкенде, а не придумывает свои.
 *
 * ЮЛ ПО УМОЛЧАНИЮ — как на сайте. С ИП «Работодатели» было 7 899 против 4 806
 * на плитке сайта, и открывший оба экрана видел два разных ответа.
 *
 * ИП ПРИХОДЯТ ПОД КЛЮЧАМИ ЮЛ. `subject_kind=ip` подставляет поля ИП под те же
 * имена; только `ul_active` остаётся числом ЮЛ, а работодатели-ИП — это
 * `td_opv_employer_cnt`. ИП без ОСНС не имеют КАТО и лежат в строке
 * «Не указано», поэтому по району ОСНС у ИП неизвестна — `null`, а не 0.
 */

export type LaborPeriod = 'week' | 'month' | 'quarter'
export type LaborSegment = 'all' | 'ul' | 'ip'

/*
 * `value: number | null` — не перестраховка: «Без ОСНС» по ИП в разрезе района
 * неизвестна (см. выше). `Kpi` рисует `null` прочерком, а `0` соврал бы
 * «никого нет».
 */
export type LaborKpi = {
  id: string
  label: string
  value: number | null
  previous: number | null
  deltaPercent: number | null
  hint?: string
  lowerIsBetter?: boolean
}

export type LaborDistrictRow = {
  districtName: string
  employers: number
  payRemainEmployers: number
  ehrRemainEmployers: number
  osns: number | null
}

export type LaborDashboard = {
  territory: string
  unit: LaborPeriod
  segment: LaborSegment
  extractDate: string | null
  previousExtractDate: string | null
  sourceNote: string
  kpis: LaborKpi[]
  complianceKpis: LaborKpi[]
  dynamics: Array<{
    label: string
    key: string
    employers: number
    payRemainEmployers: number
    ehrRemainEmployers: number
  }>
  sizeStructure: Array<{ name: string; value: number }>
  complianceBreakdown: Array<{ name: string; value: number }>
  payBySize: Array<{ name: string; value: number }>
  districts: LaborDistrictRow[]
}

export const LABOR_SEGMENTS: { value: LaborSegment; label: string }[] = [
  { value: 'ul', label: 'ЮЛ' },
  { value: 'ip', label: 'ИП' },
  { value: 'all', label: 'ЮЛ + ИП' },
]

const OBLAST_NAME = 'Атырауская область'
/** Сколько последних срезов в динамике — как у бэкенда (`DYNAMICS_SLICES`). */
const DYNAMICS_SLICES = 12
const LOOKBACK_DAYS: Record<LaborPeriod, number> = { week: 7, month: 30, quarter: 90 }

/* ─── Ручки ─── */

type VliSync = { extract_date: string; synced_at?: string }

type VliRow = {
  territory_id: number | null
  region: string
  ul_active: number
  td_opv_employer_cnt: number
  pay_remain_employer_cnt: number
  pay_remain_people: number
  ehr_remain_employer_cnt: number
  ehr_remain_people: number
  pay_ge10_employer_cnt: number
  osns_ul_cnt: number
  size_small_count: number
  size_medium_count: number
  size_large_count: number
  pay_size_small_cnt: number
  pay_size_medium_cnt: number
  pay_size_large_cnt: number
}

type VliStatistics = {
  meta: { extract_date: string; subject_kind: 'ul' | 'ip' }
  rows: VliRow[]
  overall: VliRow
}

function fetchVliSyncs(): Promise<VliSync[]> {
  return api.get<VliSync[]>('/vli/syncs')
}

function fetchVliStatistics(kind: 'ul' | 'ip', extractDate: string): Promise<VliStatistics> {
  return api.get<VliStatistics>('/vli/statistics', {
    params: { subject_kind: kind, extract_date: extractDate },
  })
}

/* ─── Срез: ЮЛ и ИП одной датой ─── */

type Slice = {
  extractDate: string
  ul: VliStatistics
  ip: VliStatistics | null
  /** В срезе есть разбивка ИП (признак среза, а не района). */
  hasIp: boolean
  /** ИП без ОСНС разложены по районам (иначе все они в «Не указано»). */
  ipOsnsPlaced: boolean
}

async function loadSlice(extractDate: string): Promise<Slice> {
  const [ul, ip] = await Promise.all([
    fetchVliStatistics('ul', extractDate),
    fetchVliStatistics('ip', extractDate).catch(() => null),
  ])
  const hasIp = !!ip && (Number(ip.overall?.td_opv_employer_cnt) || 0) > 0
  const ipDistrictOsns = (ip?.rows ?? [])
    .filter((r) => r.territory_id != null)
    .reduce((s, r) => s + (Number(r.osns_ul_cnt) || 0), 0)
  const ipOsnsTotal = Number(ip?.overall?.osns_ul_cnt) || 0
  return { extractDate, ul, ip, hasIp, ipOsnsPlaced: ipOsnsTotal === 0 || ipDistrictOsns > 0 }
}

/* ─── Метрики ─── */

type Metrics = {
  employersActive: number
  tdOpv: number
  payEmployers: number
  payPeople: number
  ehrEmployers: number
  ehrPeople: number
  payGe10: number
  osns: number | null
  osnsWithoutIp: boolean
  sizeSmall: number
  sizeMedium: number
  sizeLarge: number
  paySmall: number
  payMedium: number
  payLarge: number
}

const n = (v: unknown): number => Number(v) || 0

/** Витрина пишет город как «Атырау», в нашем справочнике — «г. Атырау». */
function districtOfRow(region: string): string {
  return region.trim() === 'Атырау' ? 'г. Атырау' : region.trim()
}

function rowOf(stat: VliStatistics | null, district: string | null): VliRow | null {
  if (!stat) return null
  if (!district) return stat.overall
  return stat.rows.find((r) => r.territory_id != null && districtOfRow(r.region) === district) ?? null
}

function ulMetrics(r: VliRow | null): Metrics {
  return {
    employersActive: n(r?.ul_active),
    tdOpv: n(r?.td_opv_employer_cnt),
    payEmployers: n(r?.pay_remain_employer_cnt),
    payPeople: n(r?.pay_remain_people),
    ehrEmployers: n(r?.ehr_remain_employer_cnt),
    ehrPeople: n(r?.ehr_remain_people),
    payGe10: n(r?.pay_ge10_employer_cnt),
    osns: n(r?.osns_ul_cnt),
    osnsWithoutIp: false,
    sizeSmall: n(r?.size_small_count),
    sizeMedium: n(r?.size_medium_count),
    sizeLarge: n(r?.size_large_count),
    paySmall: n(r?.pay_size_small_cnt),
    payMedium: n(r?.pay_size_medium_cnt),
    payLarge: n(r?.pay_size_large_cnt),
  }
}

function ipMetrics(r: VliRow | null, osnsKnown: boolean): Metrics {
  return {
    employersActive: n(r?.td_opv_employer_cnt),
    tdOpv: n(r?.td_opv_employer_cnt),
    payEmployers: n(r?.pay_remain_employer_cnt),
    payPeople: n(r?.pay_remain_people),
    ehrEmployers: n(r?.ehr_remain_employer_cnt),
    ehrPeople: n(r?.ehr_remain_people),
    payGe10: n(r?.pay_ge10_employer_cnt),
    osns: osnsKnown ? n(r?.osns_ul_cnt) : null,
    osnsWithoutIp: false,
    sizeSmall: 0,
    sizeMedium: 0,
    sizeLarge: 0,
    paySmall: 0,
    payMedium: 0,
    payLarge: 0,
  }
}

function scopeMetrics(slice: Slice, district: string | null, segment: LaborSegment): Metrics {
  const ul = ulMetrics(rowOf(slice.ul, district))
  if (segment === 'ul' || !slice.hasIp) return ul
  // По области ОСНС у ИП известна всегда; по району — только если разложена.
  const ipOsnsKnown = !district || slice.ipOsnsPlaced
  const ip = ipMetrics(rowOf(slice.ip, district), ipOsnsKnown)
  if (segment === 'ip') return ip
  return {
    employersActive: ul.employersActive + ip.employersActive,
    tdOpv: ul.tdOpv + ip.tdOpv,
    payEmployers: ul.payEmployers + ip.payEmployers,
    payPeople: ul.payPeople + ip.payPeople,
    ehrEmployers: ul.ehrEmployers + ip.ehrEmployers,
    ehrPeople: ul.ehrPeople + ip.ehrPeople,
    payGe10: ul.payGe10 + ip.payGe10,
    osns: ip.osns === null ? ul.osns : n(ul.osns) + ip.osns,
    osnsWithoutIp: ip.osns === null,
    sizeSmall: ul.sizeSmall,
    sizeMedium: ul.sizeMedium,
    sizeLarge: ul.sizeLarge,
    paySmall: ul.paySmall,
    payMedium: ul.payMedium,
    payLarge: ul.payLarge,
  }
}

function pctDelta(now: number, base: number): number | null {
  if (!base) return null
  return Math.round(((now - base) / base) * 1000) / 10
}

function dayMs(iso: string): number {
  return Date.parse(`${iso}T00:00:00Z`)
}

/** Предыдущий срез — ближайший к «последний − 7/30/90 дней», запасной — второй по свежести. */
function findPrevious(dates: string[], unit: LaborPeriod): string | null {
  const [first, ...rest] = dates
  if (!first || rest.length === 0) return null
  const latest = dayMs(first)
  const target = latest - LOOKBACK_DAYS[unit] * 86_400_000
  let best: string | null = null
  let bestDiff = Number.POSITIVE_INFINITY
  for (const d of rest) {
    const t = dayMs(d)
    const diff = Math.abs(t - target)
    if (t <= latest && diff < bestDiff) {
      best = d
      bestDiff = diff
    }
  }
  return best ?? rest[0] ?? null
}

function formatDay(iso: string): string {
  const [y, m, d] = iso.split('-')
  return `${d}.${m}.${y}`
}

/* ─── Сборка экрана ─── */

async function fetchLaborDashboardRaw(params: {
  unit: LaborPeriod
  /** Район из справочника планшета (`ATYRAU_DISTRICTS`); пусто — вся область. */
  district?: string | null
  segment?: LaborSegment
}): Promise<LaborDashboard> {
  const unit = params.unit
  const segment = params.segment ?? 'ul'
  const district = params.district || null

  const syncs = await fetchVliSyncs()
  const dates = [...syncs].map((s) => s.extract_date).sort((a, b) => b.localeCompare(a))
  const latestDate = dates[0]
  if (!latestDate) throw new Error('В витрине ВЛИ ещё нет ни одного среза')

  const previousDate = findPrevious(dates, unit)
  const dynamicsDates = dates.slice(0, DYNAMICS_SLICES).reverse()
  const needed = [...new Set([latestDate, ...(previousDate ? [previousDate] : []), ...dynamicsDates])]
  const slices = new Map(
    await Promise.all(needed.map(async (d) => [d, await loadSlice(d)] as const)),
  )
  const latest = slices.get(latestDate)!
  const previous = previousDate ? slices.get(previousDate)! : null

  const curr = scopeMetrics(latest, district, segment)
  // Сопоставимая база: если в прошлом срезе ИП ещё не было, дельту считаем по ЮЛ.
  const deltaUlOnly = segment === 'all' && latest.hasIp && !!previous && !previous.hasIp
  const prev = previous ? scopeMetrics(previous, district, deltaUlOnly ? 'ul' : segment) : null

  const makeKpi = (
    id: string,
    label: string,
    key: keyof Metrics,
    hint: string,
    lowerIsBetter: boolean,
  ): LaborKpi => {
    const value = curr[key] as number | null
    const base = prev ? (prev[key] as number | null) : null
    return {
      id,
      label,
      value,
      previous: deltaUlOnly ? null : base,
      deltaPercent: base === null || value === null ? null : pctDelta(value, base),
      hint: deltaUlOnly ? `${hint} · Δ по ЮЛ` : hint,
      lowerIsBetter,
    }
  }

  const ipOsnsTotal = n(latest.ip?.overall?.osns_ul_cnt)
  const osnsNote =
    district && curr.osns === null
      ? `ИП без ОСНС приходят без КАТО и на районы не раскладываются; по области — ${ipOsnsTotal.toLocaleString('ru-RU')}`
      : district && curr.osnsWithoutIp
        ? `только ЮЛ; ИП без ОСНС (${ipOsnsTotal.toLocaleString('ru-RU')}) — только по области`
        : null

  const kpis = [
    makeKpi(
      'employers',
      segment === 'ip' ? 'ИП с ТД/ОПВ' : segment === 'ul' ? 'Активные ЮЛ' : 'Работодатели (ЮЛ+ИП)',
      'employersActive',
      segment === 'ip' ? 'ВЛИ · ИП' : 'ВЛИ · активные работодатели',
      false,
    ),
    makeKpi('payEmployers', 'Без трудовых договоров', 'payEmployers', 'работодатели: ОПВ платятся, договора в ЕСУТД нет', true),
    makeKpi('payPeople', 'Работников без трудового договора', 'payPeople', 'человек: взносы есть, договора нет', true),
    makeKpi('ehr', 'Риск задолженности по ЗП', 'ehrEmployers', 'работодатели: договор в ЕСУТД есть, ОПВ нет', true),
  ]

  const complianceKpis = [
    makeKpi('osns', 'Без ОСНС', 'osns', osnsNote ? `обязательное соцстрахование · ${osnsNote}` : 'обязательное соцстрахование', true),
    makeKpi('payGe10', '10+ работников без трудового договора', 'payGe10', 'работодатели, у которых 10 и более работников без договора', true),
    makeKpi('tdOpv', 'С ТД / ОПВ', 'tdOpv', 'работодатели в контуре', false),
    makeKpi('ehrPeople', 'Работников с риском задолженности ЗП', 'ehrPeople', 'человек: договор есть, отчислений нет', true),
  ]

  const dynamics = dynamicsDates.map((d) => {
    const m = scopeMetrics(slices.get(d)!, district, segment)
    return {
      label: formatDay(d),
      key: d,
      employers: m.employersActive,
      payRemainEmployers: m.payEmployers,
      ehrRemainEmployers: m.ehrEmployers,
    }
  })

  const districts = ATYRAU_DISTRICTS.filter((d) => !district || d === district).map((d) => {
    const m = scopeMetrics(latest, d, segment)
    return {
      districtName: d,
      employers: m.employersActive,
      payRemainEmployers: m.payEmployers,
      ehrRemainEmployers: m.ehrEmployers,
      osns: m.osns,
    }
  })

  const noteParts = [
    `ВЛИ (перечни ГИТ), витрина app.vli_statistic, срез ${formatDay(latestDate)}; те же ручки, что у сайта (/vli/statistics).`,
    district
      ? `${district} — фактические цифры района по КАТО.`
      : 'Область — сумма всех строк витрины, включая «Не указано».',
  ]
  if (!previous) noteParts.push('Срез пока один — сравнивать не с чем, динамика появится со следующей выгрузкой.')
  if (segment !== 'ul' && !latest.ipOsnsPlaced) {
    noteParts.push(`ИП без ОСНС (${ipOsnsTotal.toLocaleString('ru-RU')}) в перечне без КАТО — учтены только в итоге области.`)
  }

  return {
    territory: district ?? OBLAST_NAME,
    unit,
    segment,
    extractDate: latestDate,
    previousExtractDate: previousDate,
    sourceNote: noteParts.join(' '),
    kpis,
    complianceKpis,
    dynamics,
    sizeStructure:
      segment === 'ip'
        ? [{ name: 'ИП в контуре', value: curr.employersActive }]
        : [
            { name: 'Малые', value: curr.sizeSmall },
            { name: 'Средние', value: curr.sizeMedium },
            { name: 'Крупные', value: curr.sizeLarge },
          ],
    complianceBreakdown: [
      { name: 'Без трудовых договоров', value: curr.payEmployers },
      { name: 'Риск задолженности ЗП', value: curr.ehrEmployers },
      {
        name:
          curr.osns === null
            ? 'Без ОСНС (нет разреза по району)'
            : curr.osnsWithoutIp
              ? 'Без ОСНС (только ЮЛ)'
              : 'Без ОСНС',
        value: curr.osns ?? 0,
      },
      { name: '10+ без трудового договора', value: curr.payGe10 },
    ],
    payBySize:
      segment === 'ip'
        ? [{ name: 'ИП', value: curr.payEmployers }]
        : [
            { name: 'Малые', value: curr.paySmall },
            { name: 'Средние', value: curr.payMedium },
            { name: 'Крупные', value: curr.payLarge },
          ],
    districts,
  }
}

/**
 * Экран целиком — в `screenCache` на час: собранная сводка весит килобайты и
 * переживает перезагрузку вкладки, повторное открытие — без запросов. Сырые
 * ответы при этом живут только в памяти (`api.ts`). Кнопка «Обновить» сбрасывает
 * ключ через `invalidateScreenCache`.
 */
export function fetchLaborDashboard(params: {
  unit: LaborPeriod
  /** Район из справочника планшета (`ATYRAU_DISTRICTS`); пусто — вся область. */
  district?: string | null
  segment?: LaborSegment
}): Promise<LaborDashboard> {
  return cachedCall(`labor/dashboard:${params.unit}:${params.district || 'all'}:${params.segment ?? 'ul'}`, () => fetchLaborDashboardRaw(params))
}
